import { useRef, useState } from 'react';
import { api, HttpError } from '../api/client';
import { bustRender } from '../api/cacheBust';
import type {
  QaReviewedItem, QaVerdictValue, RecutState, RenderRow, ReprocessMethod,
} from '../api/types';
import { ProductDiagnosticModal } from './ProductDiagnosticModal';

interface QaReviewCardProps {
  item: QaReviewedItem;
  storeId: string;
  busy?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  onOverride: (verdict: QaVerdictValue) => void;
  onZoom: () => void;
  /** Called after a card-local action (run-now approve, reprocess-as, reject,
   * manual SAM fix) so the gallery can refresh counts/list. */
  onChanged?: () => void;
  /** Surface a prominent gallery-level toast (success/failure). Without this the
   * only feedback was a faint 10px inline line — a failed "Reprocess as" read as
   * "nothing happened" (bug B). */
  onToast?: (msg: string) => void;
}

// SEK, Swedish grouping, no decimals — the value badge that explains a card's rank.
const SEK_FMT = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 });
const fmtSek = (n: number) => `${SEK_FMT.format(n)} kr`;

/** Tier options for the per-card "Reprocess as" queue control — mirrors the
 * ReprocessDialog method <select> (these are the queue-able methods). */
const REPROCESS_TIERS: ReprocessMethod[] = [
  'tier_0', 'tier_1', 'tier_2', 'ben2', 'floodfill', 'passthrough', 'snapedit',
];

/** A product parked for manual handling carries an operator 'fail', and
 * record_verdicts stamps remediation_status='requested' on every fail — so the card
 * would show the pulsing "⟳ queued" badge as if a SnapEdit job were in flight.
 * Nothing is actually spawned (the route's remediate flag defaults to false), but in
 * a bucket that means "waiting for a human" it reads as "being worked on". */
function isParkedForManual(item: QaReviewedItem): boolean {
  return item.human_verdict === 'fail' && (item.human_reason ?? '').startsWith('manual:');
}

/** Re-cut batch badge, so the operator can see batch state from any bucket. */
function RecutBadge({ state }: { state: RecutState }) {
  const queued = state === 'queued';
  return (
    <span
      data-testid="recut-badge"
      title={queued
        ? 'Un-approved and queued — re-cuts on the next job run'
        : 'Re-cut has landed — this is the new cutout, awaiting your review'}
      className={
        'ff-chip text-[10px] ' +
        (queued ? 'bg-ff-sand/60 text-ff-forest animate-pulse' : 'bg-ff-mint/50 text-ff-forest')
      }
    >
      {queued ? '⟳ re-cut queued' : '⟳ re-cut — review'}
    </span>
  );
}

/** Small badge reflecting an async remediation's status. Renders nothing for
 * "none"/null (the common case — most cards have no remediation in flight). */
function RemediationBadge({ status }: { status: string | null }) {
  const spec: Record<string, { className: string; label: string }> = {
    requested: { className: 'bg-ff-mint/30 text-ff-forest animate-pulse', label: '⟳ queued' },
    done: { className: 'bg-ff-mint/50 text-ff-forest', label: '✓ remediated' },
    failed: { className: 'bg-ff-danger-soft text-ff-danger', label: '✗ remediation failed' },
  };
  const s = status ? spec[status] : undefined;
  if (!s) return null;
  return (
    <span data-testid="remediation-badge" className={'ff-chip text-[10px] ' + s.className}>
      {s.label}
    </span>
  );
}

/** Badge for a verdict. `who` is the label prefix ("AI" / "You"). */
function VerdictBadge({ who, verdict }: { who: string; verdict: QaVerdictValue }) {
  const pass = verdict === 'pass';
  return (
    <span
      className={
        'ff-chip text-[10px] ' +
        (pass
          ? 'bg-ff-mint/50 text-ff-forest'
          : 'bg-ff-danger-soft text-ff-danger')
      }
    >
      {who} {pass ? '✓ approved' : '✗ disapproved'}
    </span>
  );
}

// Our cutout objects live at <prefix>/cutouts-v2/<sha>_<tier|sam|manual>.png.
const CUTOUT_PATH = '/cutouts-v2/';

/** Is this pinned URL one of OUR cutout objects, or a raw source photo?
 *
 * The distinction is the whole bug. `approved_image_url` carries two different things
 * depending on which flow wrote it: SAM / manual-upload / Run-now pin a real cutout
 * object, while pin-on-approve records the URL to cut FROM — an unprocessed product
 * photo. Measured 2026-08-12: ALL 3,060 live milrab-no pins are source photos.
 */
export function isOurCutout(url: string | null | undefined): boolean {
  return Boolean(url && url.includes(CUTOUT_PATH));
}

/**
 * Should an operator's pin stand in for the composed ad?
 *
 * Only when it is one of our cutout objects AND the render hasn't caught up to it. The
 * pin exists for the SAM / manual-upload / Run-now flows: a fresh cutout is approved
 * while the stored render is still pre-fix, so showing that render would hide the fix.
 * Once a render lands after the pin, the render already reflects it and IS the thing to
 * review.
 *
 * A raw source photo never qualifies. That was live: 82 milrab-no products showed an
 * unprocessed product photo captioned "✓ approved cutout", so the reviewer had nothing
 * to judge. Note that timing alone CANNOT catch it — pin-on-approve stamps the approval
 * after the render it approves, so all 82 of those pins looked "fresh". Check what the
 * URL points at first; treat the timestamps only as a tie-break.
 */
export function shouldShowPin(
  approvedImageUrl: string | null | undefined,
  approvedAt: string | null | undefined,
  renderAt: number | null | undefined,
): boolean {
  if (!isOurCutout(approvedImageUrl)) return false;
  if (!approvedAt || renderAt == null) return true;    // a real cutout, but undatable
  const pinnedMs = Date.parse(approvedAt);
  return !Number.isFinite(pinnedMs) || pinnedMs > renderAt * 1000;  // render_at is SECONDS
}

export function QaReviewCard({
  item, storeId, busy, selectable, selected, onToggleSelect, onOverride, onZoom, onChanged,
  onToast,
}: QaReviewCardProps) {
  const effective = item.effective_verdict;
  const showPin = shouldShowPin(
    item.approved_image_url, item.approved_at, item.render_at);
  const [running, setRunning] = useState(false);
  // The landed re-cut, shown INLINE so the card doesn't move/disappear before
  // the operator has seen the new image and decided (operator feedback,
  // 2026-08-27): approve here = the same pin-on-approve as ✓ Approve.
  const [recut, setRecut] = useState<RenderRow | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  // Own busy flag for "Reprocess as" — was sharing actionBusy with approve/reject,
  // so an in-flight sibling action could leave the button disabled and the click
  // would silently no-op (bug B, RANK 1).
  const [reprocessBusy, setReprocessBusy] = useState(false);
  const [diagnosticOpen, setDiagnosticOpen] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // Reprocess-as tier defaults to the suggested next tier (escalate suggestion),
  // else tier_1. The operator can change it before queueing.
  const [reprocessTier, setReprocessTier] = useState<ReprocessMethod>(
    (item.suggested_tier as ReprocessMethod) || 'tier_1',
  );

  const parked = isParkedForManual(item);

  const errText = (e: unknown) =>
    e instanceof HttpError ? e.detail : (e as Error).message;

  /** Disapprove + leave for manual handling. Shortened to "Manual" on the button:
   * the operator reads it as "disapproved, I'll deal with it myself". */
  /** Approve the landed re-cut: the SAME operator PASS as ✓ Approve —
   * record_verdicts pins the current cutout identity (pin-on-approve, spec
   * §6), and the current row IS the fresh re-cut, so this stores + pins it. */
  async function approveRecut() {
    setActionBusy(true);
    setMsg(null);
    try {
      await api.submitQaVerdicts(
        [{ product_id: item.product_id, verdict: 'pass', reason: 'run-now: approved re-cut' }],
        storeId,
      );
      const ok = `${item.product_id} approved — new cutout pinned.`;
      setRecut(null);
      setMsg(ok);
      onToast?.(ok);
      onChanged?.();
    } catch (e) {
      setMsg(`Approve failed: ${errText(e)}`);
    } finally {
      setActionBusy(false);
    }
  }

  async function manualPark() {
    setActionBusy(true);
    setMsg(null);
    try {
      await api.bulkManualPark(storeId, [item.product_id]);
      const ok = `${item.product_id} parked for manual handling — see the Manual tab.`;
      setMsg(ok);
      onToast?.(ok);
      onChanged?.();
    } catch (e) {
      const failure = `Manual park failed for ${item.product_id}: ${errText(e)}`;
      setMsg(failure);
      onToast?.(failure);
    } finally {
      setActionBusy(false);
    }
  }

  /** ui-light: queue a re-cut (revoke-pin-aware) + run the render job
   *  restricted to the queue, then poll until it lands (~3–5 min). The
   *  re-cut result comes back through the normal card refresh — review and
   *  approve it in the Re-cut bucket, exactly like a scheduled drain. */
  async function runNow() {
    setRunning(true);
    setMsg(null);
    try {
      const q = await api.bulkRecut(storeId, [item.product_id], reprocessTier);
      if (q.diverted_count > 0) {
        setMsg(
          `Already at ${reprocessTier} — a re-cut can't change it; parked for manual (upload a cutout).`,
        );
        onChanged?.();
        return;
      }
      const run = await api.reprocessQueueRun(storeId, 'overrides', [item.product_id]);
      setMsg('Re-cut job running for this product — typically 2–4 min…');
      const started = Date.now();
      for (;;) {
        await new Promise((r) => setTimeout(r, 10_000));
        const { status } = await api.jobExecutionStatus(storeId, run.execution);
        if (status === 'succeeded') {
          // Deliberately NO onChanged() here — a gallery refetch moves the
          // product to another bucket and the card vanishes mid-review. Fetch
          // just this product's fresh row and show it inline instead.
          try {
            setRecut(await api.getRender(storeId, item.product_id));
            setMsg(null);
          } catch {
            setMsg('Re-cut landed — find it in the Re-cut bucket to approve.');
            onChanged?.();
          }
          return;
        }
        if (status === 'failed') {
          setMsg('Render-job execution failed — the override stays queued for the next run.');
          return;
        }
        if (Date.now() - started > 20 * 60_000) {
          setMsg('Job still running after 20 min — it continues server-side; refresh later.');
          return;
        }
        setMsg(
          `Re-cut job running — ${Math.round((Date.now() - started) / 1000)}s elapsed (typically 2–4 min)…`,
        );
      }
    } catch (e) {
      setMsg(`Run failed: ${errText(e)}`);
    } finally {
      setRunning(false);
    }
  }

  async function reprocessAs() {
    setReprocessBusy(true);
    setMsg(null);
    try {
      await api.bulkEnqueueReprocess(storeId, [item.product_id], reprocessTier);
      const ok = `Queued ${item.product_id} as ${reprocessTier} — runs on the next job.`;
      setMsg(ok);
      onToast?.(ok);
      onChanged?.();
    } catch (e) {
      const failure = `Reprocess failed for ${item.product_id}: ${errText(e)}`;
      setMsg(failure);
      onToast?.(failure);
      console.error('reprocessAs failed', e);   // never a silent no-op
    } finally {
      setReprocessBusy(false);
    }
  }

  async function reject() {
    setActionBusy(true);
    setMsg(null);
    try {
      await api.rejectOverride(storeId, item.product_id);
      setMsg('Rejected — kept out of the feed.');
      onChanged?.();
    } catch (e) {
      setMsg(`Reject failed: ${errText(e)}`);
    } finally {
      setActionBusy(false);
    }
  }

  const [uploadBusy, setUploadBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function onUploadFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so re-selecting the same file fires onChange again
    if (!file) return;
    setUploadBusy(true);
    setMsg(null);
    try {
      await api.uploadCutout(storeId, item.product_id, file);
      const ok = `Uploaded cutout for ${item.product_id} — approved + locked in.`;
      setMsg(ok);
      onToast?.(ok);
      onChanged?.();
    } catch (err) {
      const failure = `Upload failed for ${item.product_id}: ${errText(err)}`;
      setMsg(failure);
      onToast?.(failure);
      console.error('uploadCutout failed', err); // never a silent no-op
    } finally {
      setUploadBusy(false);
    }
  }

  return (
    <div
      data-testid="qa-review-card"
      className={
        'ff-card overflow-hidden flex flex-col relative transition-shadow ' +
        (selectable && selected ? 'ring-2 ring-ff-orange' : '')
      }
    >
      {selectable && (
        <label
          className="absolute top-2 left-2 z-10 flex items-center justify-center w-7 h-7 rounded-md bg-ff-ivory/90 border border-ff-sand shadow-soft cursor-pointer"
          title={`Select ${item.product_id}`}
        >
          <input
            type="checkbox"
            checked={Boolean(selected)}
            onChange={onToggleSelect}
            aria-label={`Select ${item.product_id}`}
            className="w-4 h-4 accent-ff-orange cursor-pointer"
          />
        </label>
      )}
      {/* Primary tap target is SELECT (the bulk-review flow's main action), so the
          whole image toggles selection. The original-vs-final comparison moved to
          the "Reveal original" button just below. When a card isn't selectable
          (selection mode off), the image falls back to opening the comparison. */}
      <button
        type="button"
        onClick={selectable ? onToggleSelect : onZoom}
        aria-pressed={selectable ? Boolean(selected) : undefined}
        className={
          'relative block bg-ff-paper aspect-square w-full focus:outline-none focus:ring-2 focus:ring-ff-orange/30 ' +
          (selectable ? 'cursor-pointer' : '')
        }
        aria-label={selectable ? `Toggle selection for ${item.product_id}` : `Zoom ${item.product_id}`}
      >
        <img
          src={showPin ? item.approved_image_url! : bustRender(item.ad_image_url, item.render_at)}
          alt={item.title ?? item.product_id}
          loading="lazy"
          className="w-full h-full object-contain"
        />
        {showPin ? (
          <span
            data-testid="approved-cutout-badge"
            className="absolute top-1 right-1 ff-chip text-[10px] bg-ff-mint/70 text-ff-forest border border-ff-forest/30"
          >
            ✓ approved cutout
          </span>
        ) : item.cutout_image_url ? (
          <img
            src={item.cutout_image_url}
            alt="cutout"
            loading="lazy"
            className="absolute bottom-1 right-1 h-12 w-12 object-contain rounded border border-ff-sand bg-ff-ivory/90 shadow-soft"
          />
        ) : null}
        {/* Reinforce the click-to-select affordance: tint + inset ring when picked,
            on top of the card's outer ring. */}
        {selectable && selected && (
          <span
            aria-hidden
            className="absolute inset-0 bg-ff-orange/10 ring-2 ring-inset ring-ff-orange pointer-events-none"
          />
        )}
      </button>

      {/* Reveal the original-vs-final comparison (what clicking the image used to
          do). Kept right under the image, styled as a clear mint action bar (not a
          faint label) so it reads as a button and isn't overlooked. */}
      <button
        type="button"
        onClick={onZoom}
        data-testid="reveal-original"
        aria-label={`Reveal original image for comparison — ${item.product_id}`}
        className="w-full flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-medium text-ff-forest bg-ff-mint/30 border-b border-ff-sand hover:bg-ff-mint/50 transition-colors focus:outline-none focus:ring-2 focus:ring-ff-forest/40"
      >
        ⤢ Reveal original image for comparison
      </button>

      <div className="p-4 flex flex-col gap-2 grow">
        <div className="flex flex-wrap items-center gap-1.5">
          {item.ai_verdict && <VerdictBadge who="AI" verdict={item.ai_verdict} />}
          {item.human_verdict && <VerdictBadge who="You" verdict={item.human_verdict} />}
          {item.recut_state && <RecutBadge state={item.recut_state} />}
          {(item.labels ?? []).map((l) => (
            <span
              key={l}
              data-testid="label-chip"
              title="Review label — a progress marker only; it doesn't affect the product or its cutout"
              className="ff-chip text-[10px] bg-ff-sand/60 text-ff-forest"
            >
              ⚑ {l}
            </span>
          ))}
          {parked ? (
            <span
              data-testid="parked-badge"
              title="Left for manual handling — upload a cutout or use a 3rd-party API"
              className="ff-chip text-[10px] bg-ff-danger-soft text-ff-danger"
            >
              ⚑ parked for manual
            </span>
          ) : (
            <RemediationBadge status={item.remediation_status} />
          )}
          {item.cutout_tier && (
            <span
              data-testid="cutout-tier-badge"
              className="ff-chip text-[10px] bg-ff-sand/50 text-ff-mute"
              title={item.cutout_quality_reason ?? undefined}
            >
              made with {item.cutout_tier}
              {item.cutout_quality_score != null && (
                <span className="opacity-70"> · {item.cutout_quality_score.toFixed(2)}</span>
              )}
            </span>
          )}
        </div>

        <div className="font-mono text-[11px] text-ff-mute truncate" title={item.product_id}>
          {item.product_id}
        </div>
        {(item.sales_30d != null || item.stock_value != null) && (
          <div
            data-testid="value-line"
            className="text-[11px] text-ff-mute flex flex-wrap items-center gap-x-2 gap-y-0.5"
          >
            {item.sales_30d != null && (
              <span title="Net product revenue ex-VAT, last 30 days">
                30d sales{' '}
                <span className="text-ff-forest font-medium">{fmtSek(item.sales_30d)}</span>
              </span>
            )}
            {item.stock_value != null && (
              <span title="Free-stock value at cost">
                stock{' '}
                <span className="text-ff-forest font-medium">{fmtSek(item.stock_value)}</span>
              </span>
            )}
          </div>
        )}
        {item.title && (
          <div className="text-sm text-ff-forest leading-snug line-clamp-2">{item.title}</div>
        )}
        {item.ai_reason && (
          <div className="text-xs text-ff-mute italic leading-snug">“{item.ai_reason}”</div>
        )}

        {item.suggested_action && (
          <div data-testid="suggestion-hint" className="text-[11px] leading-snug">
            {item.suggested_action === 'escalate' && item.suggested_tier && (
              <span className="text-ff-mute">
                Suggested: re-run as{' '}
                <button
                  type="button"
                  onClick={() => setReprocessTier(item.suggested_tier as ReprocessMethod)}
                  className="font-medium text-ff-forest underline decoration-dotted hover:text-ff-forest-soft"
                >
                  {item.suggested_tier}
                </button>
              </span>
            )}
            {item.suggested_action === 'manual' && (
              <span className="text-ff-mute">
                Tiers exhausted — upload a cutout PNG (Upload button on this card)
              </span>
            )}
            {item.suggested_action === 'third_party' && (
              <span className="text-ff-mute" title="SnapEdit API key not configured">
                Manual done — send to SnapEdit (disabled)
              </span>
            )}
          </div>
        )}

        <>
            <div className="mt-auto pt-2 flex items-center gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => onOverride('pass')}
                className={
                  'flex-1 px-2 py-1 text-xs rounded border transition-colors disabled:opacity-50 ' +
                  (effective === 'pass'
                    ? 'border-ff-forest bg-ff-mint/40 text-ff-forest'
                    : 'border-ff-sand text-ff-forest hover:bg-ff-mint/20')
                }
              >
                ✓ Approve
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => onOverride('fail')}
                className={
                  'flex-1 px-2 py-1 text-xs rounded border transition-colors disabled:opacity-50 ' +
                  (effective === 'fail'
                    ? 'border-ff-danger bg-ff-danger-soft text-ff-danger'
                    : 'border-ff-line text-ff-danger hover:bg-ff-danger-soft')
                }
              >
                ✗ Disapprove
              </button>
              <button
                type="button"
                data-testid="manual-park"
                disabled={actionBusy}
                onClick={manualPark}
                title="Disapprove and leave for manual handling — no automated re-cut
will be attempted; upload a cutout or use a 3rd-party API"
                className={
                  'flex-1 px-2 py-1 text-xs rounded border transition-colors disabled:opacity-50 ' +
                  (parked
                    ? 'border-ff-danger bg-ff-danger-soft text-ff-danger'
                    : 'border-ff-line text-ff-danger hover:bg-ff-danger-soft')
                }
              >
                ✗ Manual
              </button>
            </div>

            <div className="pt-2 flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                disabled={running}
                onClick={runNow}
                className="px-2 py-1 text-xs rounded border border-ff-forest text-ff-forest hover:bg-ff-mint/20 disabled:opacity-50"
              >
                {running ? 'Running…' : 'Run now'}
              </button>
              <button
                type="button"
                onClick={() => setDiagnosticOpen(true)}
                className="px-2 py-1 text-xs rounded border border-ff-sand text-ff-mute hover:text-ff-forest"
                data-testid="diagnose-open"
              >
                Diagnose
              </button>
              <button
                type="button"
                disabled={actionBusy}
                onClick={reject}
                className="px-2 py-1 text-xs rounded border border-ff-line text-ff-danger hover:bg-ff-danger-soft disabled:opacity-50"
              >
                Reject
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png"
                className="hidden"
                data-testid="upload-cutout-input"
                aria-label={`Upload cutout for ${item.product_id}`}
                onChange={onUploadFile}
              />
              <button
                type="button"
                disabled={uploadBusy}
                onClick={() => fileInputRef.current?.click()}
                className="px-2 py-1 text-xs rounded border border-ff-sand border-dashed text-ff-mute hover:text-ff-forest disabled:opacity-50"
              >
                {uploadBusy ? 'Uploading…' : '⬆ Upload cutout'}
              </button>
            </div>

            <div className="pt-1 flex items-center gap-1.5">
              <select
                aria-label="Reprocess tier"
                value={reprocessTier}
                onChange={(e) => setReprocessTier(e.target.value as ReprocessMethod)}
                className="rounded border border-ff-sand bg-white px-1.5 py-1 text-[11px] focus:outline-none focus:ring-2 focus:ring-ff-forest/20"
              >
                {REPROCESS_TIERS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <button
                type="button"
                disabled={reprocessBusy}
                onClick={reprocessAs}
                className="px-2 py-1 text-[11px] rounded border border-ff-sand text-ff-mute hover:text-ff-forest disabled:opacity-50"
              >
                {reprocessBusy ? 'Queuing…' : 'Reprocess as'}
              </button>
            </div>

            {running && (
              <div className="flex items-center gap-2 pt-1 text-[11px] text-ff-mute">
                <span className="animate-spin h-3 w-3 border-2 border-ff-forest border-t-transparent rounded-full" />
                Re-cut job running — typically 2–4 minutes…
              </div>
            )}
        </>

        {recut && (
          <div data-testid="recut-result" className="pt-2 border-t border-ff-sand/60 mt-2">
            <p className="font-mono text-[10px] uppercase tracking-widish text-ff-mute mb-1">
              New render — {recut.cutout_tier ?? '?'}
            </p>
            <img
              src={bustRender(
                recut.image_link ?? '',
                recut.rendered_at ? Date.parse(recut.rendered_at) : null,
              )}
              alt="re-cut result"
              className="w-full aspect-square object-contain rounded border border-ff-sand mb-2"
            />
            <div className="flex gap-2">
              <button
                type="button"
                disabled={actionBusy}
                onClick={approveRecut}
                data-testid="approve-recut"
                className="flex-1 px-2 py-1 text-xs rounded border border-ff-forest bg-ff-mint/40 text-ff-forest hover:bg-ff-mint/60 disabled:opacity-50"
              >
                ✓ Approve re-cut (pin)
              </button>
              <button
                type="button"
                disabled={actionBusy}
                onClick={() => {
                  setRecut(null);
                  onChanged?.();
                }}
                className="flex-1 px-2 py-1 text-xs rounded border border-ff-sand text-ff-mute hover:text-ff-forest disabled:opacity-50"
              >
                Later (Re-cut bucket)
              </button>
            </div>
          </div>
        )}

        {msg && <div className="text-[10px] text-ff-mute pt-1 leading-snug">{msg}</div>}
      </div>

      {diagnosticOpen && (
        <ProductDiagnosticModal
          storeId={storeId}
          productId={item.product_id}
          onClose={() => setDiagnosticOpen(false)}
          onToast={onToast}
        />
      )}

    </div>
  );
}
